import { useFleetCompanyOnboardingStatus, useFleetContracts, useUser } from '@cobliteam/hooks';
import { getLdClientContextKey, getLdContextKey, setLdClientContextKey, writeLdBootContext } from '@cobliteam/hooks/src/useFeatureFlags/ldBootContext';
import { type LDContext, useLDClient } from '@launchdarkly/react-sdk';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import InitialLoadingPage from '../pages/LoadingPage/InitialLoadingPage';
import resolveLdUserAttributes from './FeatureFlagsProvider/helpers/resolveLdUserAttributes';

interface FeatureFlagsProviderProps {
  children: React.ReactNode;
}

const COMPANY_ID_STORAGE_KEY = 'companyId';
const ONBOARDING_STATUS_STORAGE_KEY = 'onboardingStatus';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

interface CachedValue<T> {
  value: T;
  timestamp: number;
}

const getCacheKey = (prefix: string, fleetId: string) => `${prefix}_${fleetId}`;

const getCachedValue = <T,>(key: string): T | null => {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    // Invalidate old format (pre-TTL cache without { value, timestamp } wrapper)
    if (!parsed || typeof parsed !== 'object' || !('value' in parsed) || !('timestamp' in parsed)) {
      window.localStorage.removeItem(key);
      return null;
    }
    const cached = parsed as CachedValue<T>;
    if (Date.now() - cached.timestamp > CACHE_TTL_MS) {
      window.localStorage.removeItem(key);
      return null;
    }
    return cached.value;
  } catch {
    window.localStorage.removeItem(key);
    return null;
  }
};

const setCachedValue = <T,>(key: string, value: T) => {
  if (typeof window === 'undefined') return;
  const cached: CachedValue<T> = { value, timestamp: Date.now() };
  window.localStorage.setItem(key, JSON.stringify(cached));
};

interface OnboardingStatusCache {
  hasAnyOnboardingContract: boolean;
  hasAnyContractSignedInLast90Days: boolean;
}

const IS_DEMO_MODE = process.env.REACT_APP_DEMO_MODE === 'true';

const identify = (ldClient: NonNullable<ReturnType<typeof useLDClient>>, context: LDContext, key: string) => {
  setLdClientContextKey(key);
  return ldClient.identify(context).catch((error: unknown) => {
    // Sem saber em que contexto o client ficou, o próximo login com esta chave identifica de novo.
    if (getLdClientContextKey() === key) setLdClientContextKey(null);
    throw error;
  });
};

const FeatureFlagsProvider = ({ children }: FeatureFlagsProviderProps) => {
  const ldClient = useLDClient();
  // `initialized` só flipa true depois de `ldClient.identify()` resolver — antes disso, o
  // LD SDK está no contexto anônimo inicial passado ao `createLDReactProvider`. Flags como
  // `livestreamPage` avaliam para `false` no contexto anônimo, o que fazia o `PrivateRoute`
  // redirecionar rotas gated para /404 no cold boot direto por URL.
  // Uma variante otimista aqui exige distinguir "flag ausente" de "flag = false", o que o
  // shape atual não permite. O browser SDK v4 cacheia flags em localStorage por padrão, o
  // que mantém `useFlags()` com leituras síncronas uma vez que este flip ocorra.
  const [initialized, setInitialized] = useState(IS_DEMO_MODE);

  const { session, useGetUserData } = useUser();
  const { data: user, isError: hasUserDataFailed } = useGetUserData(session?.user.id, !!session);
  // `null` = query ainda em voo, espera. Objeto = pode identificar (real ou defaults seguros).
  const ldUserAttributes = resolveLdUserAttributes({ userData: user, hasUserDataFailed });

  const companyIdsCacheKey = session ? getCacheKey(COMPANY_ID_STORAGE_KEY, session.fleet.id) : '';
  const onboardingCacheKey = session ? getCacheKey(ONBOARDING_STATUS_STORAGE_KEY, session.fleet.id) : '';

  const cachedCompanyIds = useMemo(() => (session ? getCachedValue<string[]>(companyIdsCacheKey) : null), [session, companyIdsCacheKey]);
  const cachedOnboarding = useMemo(() => (session ? getCachedValue<OnboardingStatusCache>(onboardingCacheKey) : null), [session, onboardingCacheKey]);

  const { data: fleetContracts, isLoading: isLoadingContracts } = useFleetContracts(session?.fleet.id || '', {
    enabled: !!session?.fleet.id && !cachedCompanyIds,
  });
  const { data: onboardingStatus, isLoading: isLoadingOnboarding } = useFleetCompanyOnboardingStatus(session?.fleet.id || '', {
    enabled: !!session?.fleet.id && !cachedOnboarding,
  });

  const getCompanyIds = useCallback((): string[] => {
    if (cachedCompanyIds) return cachedCompanyIds;
    return fleetContracts?.map((contract) => contract.companyId) ?? [];
  }, [cachedCompanyIds, fleetContracts]);

  const getOnboardingStatus = useCallback((): OnboardingStatusCache | null => {
    if (cachedOnboarding) return cachedOnboarding;
    if (!onboardingStatus) return null;
    return {
      hasAnyOnboardingContract: onboardingStatus.hasAnyOnboardingContract,
      hasAnyContractSignedInLast90Days: onboardingStatus.hasAnyContractSignedInLast90Days,
    };
  }, [cachedOnboarding, onboardingStatus]);

  // Update company IDs cache when fleetContracts arrives
  useEffect(() => {
    if (fleetContracts?.some((c) => c.companyId) && session) {
      setCachedValue(
        companyIdsCacheKey,
        fleetContracts.map((contract) => contract.companyId)
      );
    }
  }, [fleetContracts, session, companyIdsCacheKey]);

  // Update onboarding status cache when data arrives
  useEffect(() => {
    if (onboardingStatus && session) {
      setCachedValue<OnboardingStatusCache>(onboardingCacheKey, {
        hasAnyOnboardingContract: onboardingStatus.hasAnyOnboardingContract,
        hasAnyContractSignedInLast90Days: onboardingStatus.hasAnyContractSignedInLast90Days,
      });
    }
  }, [onboardingStatus, session, onboardingCacheKey]);

  // Clear cache from other fleets — depends on fleet.id only to avoid spurious reruns
  const currentFleetId = session?.fleet.id;
  useEffect(() => {
    if (!currentFleetId) return;

    const prefixes = [COMPANY_ID_STORAGE_KEY, ONBOARDING_STATUS_STORAGE_KEY];
    const currentKeys = new Set(prefixes.map((p) => getCacheKey(p, currentFleetId)));

    const keysToRemove: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && prefixes.some((p) => key.startsWith(p)) && !currentKeys.has(key)) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      window.localStorage.removeItem(key);
    }
  }, [currentFleetId]);

  // Contexto completo: só existe quando contratos e onboarding já resolveram (ou vieram do cache),
  // para o identify sair uma vez só em vez de repetir quando cada um chega.
  const ldContext = useMemo<LDContext | null>(() => {
    if (!(session && ldUserAttributes) || isLoadingContracts || isLoadingOnboarding) return null;
    const onboarding = getOnboardingStatus();

    return {
      kind: 'multi',
      user: {
        key: session.user.email,
        id: session.user.id,
        fleet_name: session.fleet.name,
        fleet_id: session.fleet.id,
        phone_number: session.user.phoneNumber,
        num_devices: ldUserAttributes.numberOfDevices,
        creation_date: ldUserAttributes.creationDate,
        age_in_days: ldUserAttributes.ageInDays,
      },
      fleet: {
        key: session.fleet.id,
        name: session.fleet.name,
        num_devices: ldUserAttributes.numberOfDevices,
        company_ids: getCompanyIds(),
        is_onboarding: onboarding?.hasAnyOnboardingContract ?? false,
        has_recent_contract: onboarding?.hasAnyContractSignedInLast90Days ?? false,
      },
    };
  }, [session, ldUserAttributes, isLoadingContracts, isLoadingOnboarding, getCompanyIds, getOnboardingStatus]);
  // O refetch da session no foco troca o objeto sem mudar o conteúdo; a chave em string não muda.
  const ldContextKey = ldContext ? getLdContextKey(ldContext) : null;
  const identifiedContextKeyRef = useRef<string | null>(null);
  // Chave vigente (inclusive `null` enquanto o novo contexto carrega): uma avaliação antiga que termine
  // depois não pode gravar o contexto dela como o do próximo boot.
  const currentContextKeyRef = useRef<string | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `ldContextKey` resume o `ldContext`; o objeto muda de referência sem mudar de valor
  useEffect(() => {
    currentContextKeyRef.current = ldContextKey;
    if (IS_DEMO_MODE) {
      setInitialized(true);
      return;
    }
    if (!(ldClient && ldContext && ldContextKey) || identifiedContextKeyRef.current === ldContextKey) return;
    identifiedContextKeyRef.current = ldContextKey;

    // O client já está neste contexto (visita anterior ou identify desta aba): basta esperar a avaliação em voo.
    const evaluation = ldContextKey === getLdClientContextKey() ? ldClient.waitForInitialization({ timeout: 5 }) : identify(ldClient, ldContext, ldContextKey);

    // Falha ou timeout do LaunchDarkly não pode prender o app no splash: segue com os valores que houver.
    evaluation
      .catch(() => undefined)
      .then(() => {
        if (currentContextKeyRef.current === ldContextKey) writeLdBootContext(ldContext);
        setInitialized(true);
      });
  }, [ldClient, ldContextKey]);

  if (!initialized) return <InitialLoadingPage />;

  return children;
};

export default FeatureFlagsProvider;
